/**
 * ActivityLogsTab — 7 sub-tabs:
 *   Audit · Usage · Login · Deletions · Changes · Impersonation · Errors
 *
 * Cross-org filter shown for platform_admin+ only.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  ScrollText, LogIn, Trash2, Pencil, UserCheck, AlertCircle,
  Filter, Loader2, BarChart3,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import UsageLogsPanel from './UsageLogsPanel';

const SUB_TABS = [
  { key: 'audit', label: 'Audit Trail', icon: ScrollText },
  { key: 'usage', label: 'Usage', icon: BarChart3 },
  { key: 'login', label: 'Login History', icon: LogIn },
  { key: 'deletions', label: 'Deletions', icon: Trash2 },
  { key: 'changes', label: 'Changes', icon: Pencil },
  { key: 'impersonation', label: 'Impersonation', icon: UserCheck },
  { key: 'errors', label: 'Errors', icon: AlertCircle },
];

const ENDPOINT = {
  audit: '/activity-logs/audit',
  login: '/activity-logs/login',
  deletions: '/activity-logs/deletions',
  changes: '/activity-logs/changes',
  impersonation: '/activity-logs/impersonation',
  errors: '/activity-logs/errors',
};

function formatTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function ActionPill({ action }) {
  if (!action) return null;
  const tone = action.endsWith('.deleted') || action.includes('.deleted.') ? 'bg-red-100 text-red-700'
    : action.endsWith('.updated') ? 'bg-amber-100 text-amber-700'
    : action.endsWith('.created') ? 'bg-emerald-100 text-emerald-700'
    : action.startsWith('auth.') ? 'bg-warm-200 text-warm-800'
    : 'bg-muted text-muted-foreground';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono ${tone}`}>{action}</span>
  );
}

function DetailsModal({ entry, onClose }) {
  if (!entry) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ActionPill action={entry.action || 'event'} />
            <span className="text-sm font-normal text-muted-foreground">{formatTime(entry.created_at || entry.started_at)}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {entry.user_email && <div><span className="text-muted-foreground">User:</span> {entry.user_email}{entry.user_name ? ` (${entry.user_name})` : ''}</div>}
          {entry.actor_email && <div><span className="text-muted-foreground">Actor:</span> {entry.actor_email}</div>}
          {entry.target_role && <div><span className="text-muted-foreground">Impersonated:</span> {entry.target_role}</div>}
          {entry.org_name && <div><span className="text-muted-foreground">Org:</span> {entry.org_name}</div>}
          {entry.resource_type && <div><span className="text-muted-foreground">Resource:</span> {entry.resource_type} / <code className="text-xs">{entry.resource_id}</code></div>}
          {entry.route && <div><span className="text-muted-foreground">Route:</span> <code className="text-xs">{entry.method ? entry.method + ' ' : ''}{entry.route}</code></div>}
          {entry.ip_address && <div><span className="text-muted-foreground">IP:</span> {entry.ip_address}</div>}
          {entry.message && <div><span className="text-muted-foreground">Message:</span> {entry.message}</div>}
          {entry.details && Object.keys(entry.details).length > 0 && (
            <div>
              <div className="text-muted-foreground mb-1">Details</div>
              <pre className="bg-muted/40 rounded p-3 text-xs overflow-x-auto max-h-72">{JSON.stringify(entry.details, null, 2)}</pre>
            </div>
          )}
          {entry.stack_trace && (
            <div>
              <div className="text-muted-foreground mb-1">Stack trace</div>
              <pre className="bg-muted/40 rounded p-3 text-xs overflow-x-auto max-h-72">{entry.stack_trace}</pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ActivityLogsTab() {
  const { get } = useApi();
  const { user, impersonatingRole } = useAuth();
  const realRole = user?.role || 'trainer';
  const isPlatformAdminPlus = ['super_admin', 'admin'].includes(realRole) && !impersonatingRole;

  const [active, setActive] = useState('audit');
  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('all');
  const [errorSourceFilter, setErrorSourceFilter] = useState('all');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  // Load orgs once for cross-org filter
  useEffect(() => {
    if (!isPlatformAdminPlus) return;
    get('/organizations?limit=200').then(r => setOrgs(r.organizations || [])).catch(() => {});
  }, [get, isPlatformAdminPlus]);

  const load = useCallback(async () => {
    if (active === 'usage') return;   // Usage tab owns its own data fetching
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50', skip: String(skip) });
      if (isPlatformAdminPlus && orgFilter !== 'all') p.set('org_id', orgFilter);
      if (active === 'audit' && searchTerm) p.set('search', searchTerm);
      if ((active === 'audit' || active === 'deletions' || active === 'changes') && resourceTypeFilter !== 'all') {
        p.set('resource_type', resourceTypeFilter);
      }
      if (active === 'errors' && errorSourceFilter !== 'all') p.set('source', errorSourceFilter);
      const res = await get(`${ENDPOINT[active]}?${p.toString()}`);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [get, active, skip, orgFilter, searchTerm, resourceTypeFilter, errorSourceFilter, isPlatformAdminPlus]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 pb-12" data-testid="activity-logs-tab">
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b overflow-x-auto">
        {SUB_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => { setActive(t.key); setSkip(0); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap ${active === t.key ? 'border-b-2 border-[var(--lumyn-content)] text-[var(--lumyn-content)]' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`activity-subtab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {active === 'usage' ? (
        <UsageLogsPanel orgs={orgs} isPlatformAdminPlus={isPlatformAdminPlus} />
      ) : (
      <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {isPlatformAdminPlus && (
          <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setSkip(0); }}>
            <SelectTrigger className="w-44" data-testid="activity-org-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {orgs.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(active === 'audit' || active === 'deletions' || active === 'changes') && (
          <Select value={resourceTypeFilter} onValueChange={(v) => { setResourceTypeFilter(v); setSkip(0); }}>
            <SelectTrigger className="w-44" data-testid="activity-resource-filter"><SelectValue placeholder="All resources" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resource types</SelectItem>
              {['module', 'quiz', 'enrollment', 'user', 'org', 'folder', 'certificate', 'file', 'storage'].map(r =>
                <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {active === 'audit' && (
          <Input
            placeholder="Search action / resource id"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="w-64"
            data-testid="activity-search"
          />
        )}
        {active === 'errors' && (
          <Select value={errorSourceFilter} onValueChange={(v) => { setErrorSourceFilter(v); setSkip(0); }}>
            <SelectTrigger className="w-36" data-testid="activity-error-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="backend">Backend</SelectItem>
              <SelectItem value="frontend">Frontend</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Logs table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left p-3 w-44">Time</th>
              {active === 'errors' ? (
                <>
                  <th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Severity</th>
                  <th className="text-left p-3">Route</th>
                  <th className="text-left p-3">Message</th>
                </>
              ) : active === 'impersonation' ? (
                <>
                  <th className="text-left p-3">Actor</th>
                  <th className="text-left p-3">Original → Target</th>
                  <th className="text-left p-3 hidden lg:table-cell">Org</th>
                  <th className="text-left p-3 hidden xl:table-cell">Endpoint</th>
                </>
              ) : (
                <>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3 hidden lg:table-cell">Org</th>
                  <th className="text-left p-3">Resource</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground italic">No entries</td></tr>
            ) : logs.map((entry, idx) => {
              const id = entry.log_id || entry.error_id || `${active}-${idx}`;
              return (
                <tr
                  key={id}
                  className="border-t hover:bg-muted/30 cursor-pointer"
                  onClick={() => setDetail(entry)}
                  data-testid={`activity-row-${active}`}
                >
                  <td className="p-3 text-muted-foreground tabular-nums whitespace-nowrap">{formatTime(entry.created_at || entry.started_at)}</td>
                  {active === 'errors' ? (
                    <>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${entry.source === 'backend' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'}`}>{entry.source}</span></td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${entry.severity === 'fatal' ? 'bg-red-100 text-red-700' : entry.severity === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>{entry.severity}</span></td>
                      <td className="p-3 font-mono text-xs truncate max-w-[200px]">{entry.method ? entry.method + ' ' : ''}{entry.route || '—'}</td>
                      <td className="p-3 truncate max-w-[400px]">{entry.message}</td>
                    </>
                  ) : active === 'impersonation' ? (
                    <>
                      <td className="p-3">{entry.actor_email || entry.actor_user_id}</td>
                      <td className="p-3 text-xs"><code>{entry.actor_original_role || '?'} → {entry.target_role}</code></td>
                      <td className="p-3 hidden lg:table-cell">{entry.org_name || '—'}</td>
                      <td className="p-3 hidden xl:table-cell font-mono text-xs">{entry.method ? entry.method + ' ' : ''}{entry.endpoint || '—'}</td>
                    </>
                  ) : (
                    <>
                      <td className="p-3"><ActionPill action={entry.action} /></td>
                      <td className="p-3">{entry.user_email || entry.user_id || '—'}</td>
                      <td className="p-3 hidden lg:table-cell">{entry.org_name || '—'}</td>
                      <td className="p-3 text-xs">{entry.resource_type ? `${entry.resource_type} / ${entry.resource_id?.slice(0, 8)}…` : '—'}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {total > 50 && (
          <div className="flex items-center justify-between p-3 border-t text-sm">
            <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + 50, total)} of {total}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - 50))}>Previous</Button>
              <Button size="sm" variant="outline" disabled={skip + 50 >= total} onClick={() => setSkip(s => s + 50)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {detail && <DetailsModal entry={detail} onClose={() => setDetail(null)} />}
      </>
      )}
    </div>
  );
}
